/**
 * EngineWeakLinkCallout — "weak link" prompt for PE/ROE/ROI
 * Health pages, upgraded to optionally render a two-pane
 * financial-impact block ("If Not Addressed" / "Lift +15pts")
 * mirroring the LEI WeakLinkCallout pattern.
 *
 * The financial-impact block is OPT-IN via the `financialImpact`
 * prop so callers that don't have a dollar story to tell (or
 * where the math is different — e.g. ROI multiples) can keep
 * the compact headline-only variant.
 *
 * Props
 * ─────
 *   • entityLabel      : human-readable entity name ("Capability (C)")
 *   • score            : numeric score for that entity
 *   • overall          : org-overall score for context
 *   • metricSuffix     : '/100' | 'pts' | 'x' (matches the underlying engine)
 *   • description      : extra one-liner (e.g. "is your weakest component")
 *   • testIdPrefix     : data-testid stem
 *   • signed           : when true, render +/- on the score (for ROE variance)
 *   • financialImpact  : {
 *       realizedPct, afterFixPct, deltaPct,   // 0..100 numbers
 *       dollarsLeaking, dollarsUnlocked,       // may be null when VAS unavailable
 *       currency,
 *       liftLabel,                             // "Lift Capability +15pts"
 *       leakCopy, fixCopy,                     // footer coaching lines
 *       vasFootnote,                           // { revenue, marginPct, isDefault }
 *     }
 */
import React from 'react';
import { AlertTriangle, ArrowDown, ArrowRight, Wrench, Zap } from 'lucide-react';
import { TileInfoButton } from '../common/TileInfoButton';
import { LEAKAGE_METHODOLOGY_BODY } from '../../lib/leakageEstimate';

const formatScore = (val, suffix, signed) => {
  if (typeof val !== 'number') return '—';
  if (suffix === 'x') return `${val}${suffix}`;
  if (signed) {
    const sign = val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(1)} ${suffix}`;
  }
  return `${Math.round(val)}${suffix}`;
};

// Compact money formatter shared across engines. Matches the
// LEI WeakLinkCallout output so a CFO comparing LEI and PE
// dollar figures side-by-side sees consistent typography.
const formatMoney = (amount, currency = 'USD') => {
  if (typeof amount !== 'number' || !isFinite(amount)) return '';
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  const symbol = currency === 'USD' ? '$' : '';
  const tail = currency !== 'USD' ? ` ${currency}` : '';
  if (abs >= 1_000_000_000) return `${sign}${symbol}${(abs / 1_000_000_000).toFixed(1)}B${tail}`;
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M${tail}`;
  if (abs >= 1_000) return `${sign}${symbol}${Math.round(abs / 1_000)}K${tail}`;
  return `${sign}${symbol}${Math.round(abs)}${tail}`;
};

export default function EngineWeakLinkCallout({
  entityLabel,
  score,
  overall,
  metricSuffix = '/100',
  description = 'is your lowest-scoring area right now.',
  testIdPrefix = 'engine-weak-link',
  signed = false,
  financialImpact = null,
  modelFixCta = null,
}) {
  if (entityLabel == null) return null;

  const fi = financialImpact;
  const hasDollars =
    fi &&
    typeof fi.dollarsLeaking === 'number' &&
    typeof fi.dollarsUnlocked === 'number';

  return (
    <div
      className="p-4 rounded-lg border bg-rose-50 border-rose-200 space-y-3"
      data-testid={`${testIdPrefix}-callout`}
    >
      <div className="flex items-center gap-3">
        <div className="p-1.5 rounded-md shrink-0 bg-rose-100">
          <AlertTriangle className="h-4 w-4 text-rose-600" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-rose-700" data-testid={`${testIdPrefix}-eyebrow`}>
            Largest Value Leak
          </p>
          <p className="text-sm text-rose-900">
            <strong>{entityLabel}</strong>
            {' '}{description}
            {typeof score === 'number' && (
              <>
                {' '}Currently at{' '}
                <strong className="text-rose-700">
                  {formatScore(score, metricSuffix, signed)}
                </strong>
                {typeof overall === 'number' && (
                  <span className="text-slate-500">
                    {' '}· org overall {formatScore(overall, metricSuffix, signed)}
                  </span>
                )}
                .
              </>
            )}
          </p>
        </div>
      </div>

      {/* Financial-impact two-pane block — only rendered when the
          parent provides `financialImpact`. Matches the LEI
          WeakLinkCallout visual: left pane = dollar leak with a
          rose progress bar; right pane = unlock with an emerald
          bar. Dollar figures lead; percentages drop to the
          subtitle so the reader's eye lands on $$ first. */}
      {fi && (
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1"
          data-testid={`${testIdPrefix}-impact-waterfall`}
        >
          <div className="p-3 rounded-md border bg-white border-rose-200">
            <p className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 text-rose-700">
              <AlertTriangle className="h-3 w-3" /> Value At Risk
              <TileInfoButton
                testid={`${testIdPrefix}-leak-methodology`}
                title="Leakage range — how this is computed"
                body={LEAKAGE_METHODOLOGY_BODY}
                tone="light"
              />
            </p>
            {hasDollars ? (
              <>
                <p
                  className="text-2xl font-bold tabular-nums mt-1 text-rose-900"
                  data-testid={`${testIdPrefix}-leak-dollars`}
                >
                  {formatMoney(fi.dollarsLeaking, fi.currency)}
                  <span className="text-xs font-normal ml-1 text-rose-600">
                    /year leaking
                  </span>
                </p>
                <p className="text-[11px] mt-0.5 text-rose-600">
                  {Math.round(fi.realizedPct)}% of potential ROI realized
                </p>
              </>
            ) : (
              <p
                className="text-2xl font-bold tabular-nums mt-1 text-rose-900"
                data-testid={`${testIdPrefix}-leak-pct`}
              >
                {Math.round(fi.realizedPct)}%
                <span className="text-xs font-normal ml-1 text-rose-600">
                  of potential ROI realized
                </span>
              </p>
            )}
            <div className="mt-1.5 h-1.5 rounded-full overflow-hidden bg-rose-100">
              <div
                className="h-full transition-all bg-rose-500"
                style={{ width: `${Math.min(100, Math.max(0, fi.realizedPct))}%` }}
              />
            </div>
            <p className="text-[11px] mt-2 leading-snug text-rose-700">
              {fi.leakCopy ||
                `~${Math.round(100 - fi.realizedPct)}% of every learning dollar leaks before it reaches the business — every quarter you delay.`}
            </p>
          </div>
          <div className="p-3 rounded-md border bg-white border-emerald-200">
            <p className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 text-emerald-700">
              <Zap className="h-3 w-3" /> Value Recovery Opportunity
            </p>
            {hasDollars ? (
              <>
                <p
                  className="text-2xl font-bold tabular-nums mt-1 text-emerald-800"
                  data-testid={`${testIdPrefix}-fix-dollars`}
                >
                  +{formatMoney(fi.dollarsUnlocked, fi.currency)}
                  <span className="text-xs font-normal ml-1 text-emerald-600">
                    /year unlocked
                  </span>
                </p>
                <p className="text-[11px] mt-0.5 text-emerald-600">
                  {Math.round(fi.afterFixPct)}% realized (+{Math.round(fi.deltaPct)}pts)
                </p>
              </>
            ) : (
              <p
                className="text-2xl font-bold tabular-nums mt-1 text-emerald-800"
                data-testid={`${testIdPrefix}-fix-pct`}
              >
                {Math.round(fi.afterFixPct)}%
                <span className="text-xs font-normal ml-1 text-emerald-600">
                  (+{Math.round(fi.deltaPct)}pts unlocked)
                </span>
              </p>
            )}
            <div className="mt-1.5 h-1.5 rounded-full overflow-hidden bg-emerald-100">
              <div
                className="h-full transition-all bg-emerald-500"
                style={{ width: `${Math.min(100, Math.max(0, fi.afterFixPct))}%` }}
              />
            </div>
            <p className="text-[11px] mt-2 leading-snug text-emerald-700">
              {fi.fixCopy ||
                `A focused intervention on this single area shifts your realized ROI by roughly ${Math.round(fi.deltaPct)} percentage points.`}
            </p>
          </div>
        </div>
      )}

      <p className="text-[12px] text-rose-800/80 flex items-center gap-1.5 leading-snug">
        <ArrowDown className="h-3 w-3 shrink-0" />
        Below is your 3-step action plan, with personalised coaching context.
      </p>

      {/* Model-the-fix CTA — optional. When the parent wires an
          onClick, we render a single rose-styled pill under the
          3-step-plan nudge (matching the LEI WeakLinkCallout's
          primary CTA so the visual language is uniform across
          engines) so the CLO can one-click drop this specific
          lift into their scenario model. */}
      {modelFixCta?.onClick && (
        <div className="pt-1">
          <button
            type="button"
            onClick={modelFixCta.onClick}
            data-testid={`${testIdPrefix}-model-fix-cta`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-colors"
          >
            <Wrench className="h-4 w-4" />
            {modelFixCta.label || 'Model the fix in Portfolio Analysis'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Methodology footnote — sized so it doesn't compete with
          the headline numbers but is there for any CFO who asks
          "where did $X come from". */}
      {fi?.vasFootnote?.revenue && !fi.vasFootnote.isDefault && (
        <p
          className="text-[10px] leading-snug text-slate-500 pt-1"
          data-testid={`${testIdPrefix}-vas-footnote`}
        >
          $ figures based on {formatMoney(fi.vasFootnote.revenue, fi.currency)} annual revenue × {fi.vasFootnote.marginPct}% operating margin × 5% L&D-attributable share of operating profit (industry-standard productivity-impact band).
        </p>
      )}
      {fi?.vasFootnote?.isDefault && (
        <p
          className="text-[10px] leading-snug text-slate-500 pt-1"
          data-testid={`${testIdPrefix}-vas-default-note`}
        >
          $ figures use a generic baseline. Add your annual revenue + operating margin in <strong>Profile → Org Settings</strong> for org-specific numbers.
        </p>
      )}
    </div>
  );
}
